// @ts-nocheck
/**
 * Email + OTP verifier for the Create User form.
 *
 * v1 flow (per catalog §4):
 *   • Send Code button POSTs API_SIGNUP_EMAIL_SEND_CODE with { email }
 *   • OTP input strips non-digits and caps at 6
 *   • Verify OTP POSTs API_SIGNUP_EMAIL_VERIFY_CODE with { email, otp }
 *   • On success, response `signupEmailToken` becomes the create payload's
 *     signup_email_token — required for the final POST when
 *     `emailVerificationRequired` is true.
 *   • Any subsequent change to `email` resets the verified state.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSendSignupEmailCode, useVerifySignupEmailCode } from '../api';

interface Props {
  email: string;
  onEmailChange: (email: string) => void;
  /** Called with the token once the OTP is verified. */
  onVerified: (token: string) => void;
  /** Called whenever email changes so the parent can clear the token. */
  onReset: () => void;
  /** True when v1 gating says OTP is mandatory for this creator + role. */
  required: boolean;
  /** Set by the form once verification succeeded (survives re-renders). */
  verified: boolean;
  emailError?: string;
}

export function EmailVerifier({
  email,
  onEmailChange,
  onVerified,
  onReset,
  required,
  verified,
  emailError,
}: Props) {
  const [otp, setOtp] = useState('');
  const send = useSendSignupEmailCode();
  const verify = useVerifySignupEmailCode();

  useEffect(() => {
    if (!verified) setOtp('');
  }, [verified]);

  const handleSend = () => {
    if (!email.trim()) {
      toast.error('Enter an email first');
      return;
    }
    send.mutate(email.trim(), {
      onSuccess: () => toast.success('Verification code sent'),
      onError: (e: any) => toast.error(typeof e === 'string' ? e : e?.message || 'Failed to send code'),
    });
  };

  const handleVerify = () => {
    if (otp.length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    verify.mutate(
      { email: email.trim(), otp },
      {
        onSuccess: (data: any) => {
          const token = data?.signupEmailToken;
          if (!token) {
            toast.error('Verification failed');
            return;
          }
          onVerified(token);
          toast.success('Email verified');
        },
        onError: (e: any) => toast.error(typeof e === 'string' ? e : e?.message || 'Failed to verify'),
      },
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <div className="relative flex-1">
          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              onEmailChange(e.target.value);
              onReset();
            }}
            placeholder="user@example.com"
            className="!pl-10"
            maxLength={100}
          />
        </div>
        {required && !verified && (
          <Button
            type="button"
            variant="outline"
            onClick={handleSend}
            disabled={send.isPending || !email.trim()}
          >
            <Send className="h-4 w-4" />
            {send.isPending ? 'Sending…' : 'Send code'}
          </Button>
        )}
        {verified && (
          <span className="inline-flex items-center gap-1 rounded-md border border-success/30 bg-success/10 px-2 py-1 text-xs font-medium text-success">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Verified
          </span>
        )}
      </div>
      {emailError && (
        <p className="text-xs text-destructive">{emailError}</p>
      )}
      {required && !verified && (
        <div className="flex items-start gap-2">
          <Input
            inputMode="numeric"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            className="max-w-[160px] tracking-widest"
          />
          <Button
            type="button"
            onClick={handleVerify}
            disabled={verify.isPending || otp.length !== 6}
          >
            {verify.isPending ? 'Verifying…' : 'Verify'}
          </Button>
        </div>
      )}
    </div>
  );
}
